import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { SettingsSection, SettingsList, SettingsListItem, SettingsInfoRow } from './ui';
import {
  listCardDavPasswords,
  createCardDavPassword,
  revokeCardDavPassword,
  getCardDavConnectionInfo,
  CardDavPasswordInfo,
  CardDavConnectionInfo,
  CreateCardDavPasswordResponse,
} from '../../services/cardDavApi';
import styles from './CardDavSettings.module.css';

export interface CardDavSettingsProps {
  /**
   * When rendered inside a larger Settings screen, we avoid duplicated page
   * container padding and titles.
   */
  embedded?: boolean;
}

export const CardDavSettings: React.FC<CardDavSettingsProps> = ({ embedded = false }) => {
  const [passwords, setPasswords] = useState<CardDavPasswordInfo[]>([]);
  const [connectionInfo, setConnectionInfo] = useState<CardDavConnectionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newPasswordName, setNewPasswordName] = useState('');
  const [creatingPassword, setCreatingPassword] = useState(false);
  const [newlyCreatedPassword, setNewlyCreatedPassword] = useState<CreateCardDavPasswordResponse | null>(null);
  const [copiedPassword, setCopiedPassword] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [passwordsData, infoData] = await Promise.all([
        listCardDavPasswords(),
        getCardDavConnectionInfo(),
      ]);
      setPasswords(passwordsData);
      setConnectionInfo(infoData);
    } catch (err) {
      console.error('Error fetching CardDAV settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPasswordName.trim()) return;

    try {
      setCreatingPassword(true);
      setError(null);
      const result = await createCardDavPassword(newPasswordName.trim());
      setNewlyCreatedPassword(result);
      setNewPasswordName('');
      // Refresh the list
      const passwordsData = await listCardDavPasswords();
      setPasswords(passwordsData);
    } catch (err) {
      console.error('Error creating password:', err);
      setError('Failed to create password');
    } finally {
      setCreatingPassword(false);
    }
  };

  const handleRevokePassword = async (id: string) => {
    if (!window.confirm('Are you sure you want to revoke this password? Any devices using it will lose access.')) {
      return;
    }

    try {
      setError(null);
      await revokeCardDavPassword(id);
      // Refresh the list
      const passwordsData = await listCardDavPasswords();
      setPasswords(passwordsData);
    } catch (err) {
      console.error('Error revoking password:', err);
      setError('Failed to revoke password');
    }
  };

  const handleCopyPassword = async () => {
    if (newlyCreatedPassword) {
      try {
        await navigator.clipboard.writeText(newlyCreatedPassword.password);
        setCopiedPassword(true);
        setTimeout(() => setCopiedPassword(false), 2000);
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  const handleDismissNewPassword = () => {
    setNewlyCreatedPassword(null);
    setCopiedPassword(false);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const activePasswords = passwords.filter((p) => !p.revokedAt);
  const revokedPasswords = passwords.filter((p) => p.revokedAt);

  if (loading) {
    return (
      <div className={embedded ? styles.embeddedContainer : styles.container}>
        {!embedded && <h2 className={styles.title}>CardDAV Settings</h2>}
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  return (
    <div className={embedded ? styles.embeddedContainer : styles.container}>
      {!embedded && (
        <>
          <h2 className={styles.title}>CardDAV Settings</h2>
          <p className={styles.description}>
            Sync your CRM contacts with your iPhone or other CardDAV-compatible devices.
          </p>
        </>
      )}

      {error && <div className={styles.error}>{error}</div>}

      {/* Newly created password notice */}
      {newlyCreatedPassword && (
        <div className={styles.newPasswordNotice}>
          <h3>New App Password Created</h3>
          <p>
            <strong>Important:</strong> Copy this password now. You will not be able to see it
            again.
          </p>
          <div className={styles.passwordDisplay}>
            <code>{newlyCreatedPassword.password}</code>
            <Button variant="secondary" size="sm" onClick={handleCopyPassword}>
              {copiedPassword ? 'Copied!' : 'Copy'}
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={handleDismissNewPassword}>
            I have saved the password
          </Button>
        </div>
      )}

      {/* Connection Info */}
      {connectionInfo && (
        <SettingsSection
          title="Connection Information"
          description="Use these credentials when setting up CardDAV on your device"
        >
          <div className={styles.connectionInfo}>
            <SettingsInfoRow label="Server" value={connectionInfo.server} code />
            <SettingsInfoRow label="Username" value={connectionInfo.username} code />
          </div>

          <div className={styles.setupInstructions}>
            <h4>iPhone/iPad Setup</h4>
            <ol>
              {connectionInfo.setupInstructions.ios.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
        </SettingsSection>
      )}

      {/* Create New Password */}
      <SettingsSection
        title="Create App Password"
        description="Each device should have its own password for security"
      >
        <form onSubmit={handleCreatePassword} className={styles.createForm}>
          <input
            type="text"
            placeholder="Device name (e.g., iPhone, iPad)"
            value={newPasswordName}
            onChange={(e) => setNewPasswordName(e.target.value)}
            className={styles.input}
            maxLength={50}
          />
          <Button type="submit" variant="primary" size="md" loading={creatingPassword} disabled={!newPasswordName.trim()}>
            Create Password
          </Button>
        </form>
      </SettingsSection>

      {/* Active Passwords */}
      <SettingsSection title="Active Passwords">
        <SettingsList emptyMessage="No active passwords yet">
          {activePasswords.map((password) => (
            <SettingsListItem key={password.id}>
              <div className={styles.passwordInfo}>
                <div className={styles.passwordName}>{password.name}</div>
                <div className={styles.passwordMeta}>
                  Created {formatDate(password.createdAt)}
                  {password.lastUsedAt && ` • Last used ${formatDate(password.lastUsedAt)}`}
                </div>
              </div>
              <Button
                variant="danger"
                size="sm"
                onClick={() => handleRevokePassword(password.id)}
              >
                Revoke
              </Button>
            </SettingsListItem>
          ))}
        </SettingsList>
      </SettingsSection>

      {/* Revoked Passwords */}
      {revokedPasswords.length > 0 && (
        <SettingsSection title="Revoked Passwords">
          <SettingsList>
            {revokedPasswords.map((password) => (
              <SettingsListItem key={password.id} className={styles.revoked}>
                <div className={styles.passwordInfo}>
                  <div className={styles.passwordName}>{password.name}</div>
                  <div className={styles.passwordMeta}>
                    Revoked {formatDate(password.revokedAt)}
                  </div>
                </div>
              </SettingsListItem>
            ))}
          </SettingsList>
        </SettingsSection>
      )}
    </div>
  );
};

export default CardDavSettings;
